<template>
    <DashboardLayout>
        <div class="flex flex-column" style="min-height: calc(100vh - 80px);">
        <Breadcrumb class="breadcrumb" :model="items" />
        <div class="card flex flex-column justify-center pt-4 px-5">
            <div class="view-summaries-wrap absolute left-10 top-10 flex align-items-center" style="height: 42px;"
                v-if="currentView.type === 'list'">
                <Button label="View Summaries & Transcripts" icon="pi pi-external-link" class="p-button-link p-0"
                    @click="pushView('ai-summeries')" />
            </div>
            <div v-if="viewStack.length > 1" class="mb-4">
                <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer"
                    @click="goBack" />
            </div>

            <div v-if="currentView.type === 'ai-summeries'">
                <AiSummeries :Summaries="AISummeries" :Transcripts="Transcripts"
                @view-summary="pushView('View-AI-Summary', $event)"
                @view-transcript="pushView('View-AI-Transcript', $event)"/>
            </div>
            <div v-if="currentView.type === 'View-AI-Summary'">
                <AiSummaryView :data="selectedSummary" />
            </div>
            <div v-if="currentView.type === 'View-AI-Transcript'">
                <TranscriptView :data="selectedTranscript" />
            </div>

            <div v-if="currentView.type === 'list'">
                <div v-if="notices && notices.length > 0">
                    <NoticeList :notices="notices" @view-notice="pushView('view', $event)"
                        @new-notice="pushView('create', null)" />
                </div>
                <div v-else class="flex flex-column px-6">
                    <div class="flex justify-content-end gap-2 py-4 md:px-4 new-notice-wrap">
                        <Button label="New Notice" class="teal-button-small" @click="pushView('create', null)" />
                    </div>
                    <div class="flex flex-column align-self-center py-5">
                        <span class="Caption text-gray-700 text-center justify-content-center align-self-center">
                            You don't have any notices yet, please click "New Notice" to create one.
                        </span>
                    </div>
                </div>

            </div>

            <!-- v-show (not v-if) keeps the Quill instance alive once mounted,
                 preventing Vue's block-patch algorithm from traversing DOM nodes
                 that Quill has already modified, which causes nextSibling: null crashes. -->
            <div v-if="createViewEverOpened" v-show="currentView.type === 'create' || currentView.type === 'edit'">
                <CreateNotice :notice="selectedNotice" @refresh-notices="handleRefresh" />
            </div>

            <div v-if="currentView.type === 'view'">
                <ViewNotice :notice="selectedNotice" @refresh-notices="handleRefresh"
                    @edit-notice="pushView('edit', $event)" />
            </div>

        </div>

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-xl mx-auto py-4 px-6 mt-auto">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #1FC9A3;">Disclaimer</h3>
            <p class="text-700 text-sm m-0">
                Al-generated content is provided for educational support only.
                It may contain inaccuracies and should not be relied upon as final academic answers.
            </p>
        </div>

        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import Breadcrumb from 'primevue/breadcrumb';
import Button from "primevue/button";
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { listNotes, checkNoteLimit } from "@/api/tutor";
import { listAiSummeries, listTranscripts } from "@/api/student"; // in a later release we need to make these common APIs for both student and tutor

// Components
import NoticeList from '@/components/dashboard/tutor/tutorNotice/noticeList.vue';
import CreateNotice from '@/components/dashboard/tutor/tutorNotice/createNotice.vue';
import ViewNotice from "@/components/dashboard/tutor/tutorNotice/viewNotice.vue";
import { notifyError, notifyWarn } from "@/utils/notification";
import AiSummeries from "@/components/dashboard/student/notes/aiSummeries.vue";
import AiSummaryView from "@/components/dashboard/student/notes/AiSummaryView.vue";
import TranscriptView from "@/components/dashboard/student/notes/TranscriptView.vue";

const notices = ref([]);
const AISummeries = ref([]);
const Transcripts = ref([]);
const selectedNotice = ref(null);
const createViewEverOpened = ref(false);
const selectedSummary = ref();
const selectedTranscript = ref();

const viewStack = ref([
    { type: "list", label: "Notices", data: null }
]);

const items = computed(() => {
    return [
        { label: "Dashboard" },
        ...viewStack.value.map(v => ({ label: v.label }))
    ];
});

const currentView = computed(() =>
    viewStack.value[viewStack.value.length - 1]
);

watch(currentView, (view) => {
    if (view.type === 'create' || view.type === 'edit') {
        createViewEverOpened.value = true;
    }
});

async function pushView(type, data) {
    if (type === "view") {
        selectedNotice.value = data;
        viewStack.value.push({ type, label: "View Notice", data });
    }
    else if (type === "create") {
        try {
            await checkNoteLimit();
        } catch (error) {
            const responseData = error?.response?.data;
            const errorMsg = responseData?.message || "An error occurred while fetching notics. Please try again.";

            if (error?.response?.status === 403) {
                notifyWarn("Subscription Required", errorMsg);
            } else {
                notifyError(responseData?.status || "Error", errorMsg);
            }
            return;
        }
        selectedNotice.value = null;
        viewStack.value.push({ type, label: "Create Notice", data: null });
    }
    else if (type === "edit") {
        selectedNotice.value = data;
        viewStack.value.push({ type, label: "Edit Notice", data });
    } else if (type === "ai-summeries") {
        viewStack.value.push({ type, label: "AI Summeries", data });
    } else if (type === "View-AI-Summary") {
        selectedSummary.value = data;
        viewStack.value.push({ type, label: data.summary.studentSummary.title, data });
     } else if (type === "View-AI-Transcript") {
        selectedTranscript.value = data;
        console.log(selectedTranscript.value);
        viewStack.value.push({ type, label: "Class Transcript", data });
     }
}

function goBack() {
    if (viewStack.value.length > 1) {
        viewStack.value.pop();
        if (viewStack.value.length === 1) {
            selectedNotice.value = null;
        }
    }
}

const handleRefresh = async () => {
    await fetchNotices();
    viewStack.value = [{ type: "list", label: "Notices", data: null }];
    selectedNotice.value = null;
}

const fetchNotices = async () => {
    try {
        const res = await listNotes();
        notices.value = res.data.data || res.data || [];
    } catch (error) {
        console.error("Error fetching notices:", error);
        notices.value = [];
    }
}

const fetchAISummeries = async () => {
    try {
        const res = await listAiSummeries();
        AISummeries.value = res.data?.data || []; 
    } catch (error) {
        console.error("Error fetching AI summaries:", error);
    }
}

const fetchTranscripts = async () => {
    try {
        const res = await listTranscripts();
        Transcripts.value = res.data?.data || [];
    } catch (error) {
        console.error("Error fetching transcripts:", error);
    }
}

onMounted(() => {
    fetchNotices();
    fetchAISummeries();
    fetchTranscripts();
})
</script>

<style scoped>
.p-button-danger {
    padding: 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

.back-button {
    color: var(--gray-600);
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.teal-button-small {
    padding: 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}

.view-summaries-wrap {
    position: static !important;
    height: auto;
    margin-bottom: 12px;
    align-self: flex-start;
}

@media (max-width: 767px) {
    .breadcrumb {
        font-size: 12px;
    }
    .new-notice-wrap {
        margin-top: 12px; 
        justify-content: center !important; 
    }
}
</style>